import { createContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import type { TrackingParams } from "@/hooks/useTrackingParams";
import { saveLeadSource, getStoredLeadSource, getVisitorGeo } from "@/hooks/useLeadSource";
import { trackLogin } from "@/lib/analytics";

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  disabledReason: string | null;
  signUp: (email: string, password: string, tracking?: TrackingParams) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [disabledReason, setDisabledReason] = useState<string | null>(null);

  // ── Forcefully clear Supabase auth from localStorage (lock-safe fallback) ──
  const clearAuthStorage = useCallback(() => {
    const storageKey = `sb-uzimcnouitbynnmwndni-auth-token`;
    localStorage.removeItem(storageKey);
    localStorage.removeItem(`${storageKey}-code-verifier`);
    setUser(null);
    setSession(null);
  }, []);

  // ── Check is_active after any session is established ─────────
  // Returns true if the account is active and the caller may proceed; false
  // means the session was revoked and the caller should drop the session.
  const checkAccountStatus = useCallback(async (u: User | null): Promise<boolean> => {
    if (!u) return true;
    const { data } = await supabase
      .from("profiles")
      .select("is_active, disabled_reason")
      .eq("id", u.id)
      .maybeSingle();

    if (data && data.is_active === false) {
      try {
        await supabase.auth.signOut();
      } catch {
        clearAuthStorage();
      }
      setDisabledReason(data.disabled_reason ?? "unspecified");
      setUser(null);
      setSession(null);
      return false;
    }
    return true;
  }, [clearAuthStorage]);

  useEffect(() => {
    // Safety timeout: if loading stays true for 8+ seconds (e.g., INITIAL_SESSION never
    // fired or got stuck), force loading=false to prevent permanent spinner.
    const safetyTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn("[AuthContext] Safety timeout: forcing loading=false after 8s");
          setUser(null);
          setSession(null);
        }
        return false;
      });
    }, 8000);

    // SINGLE subscription for the entire app lifetime
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        // INITIAL_SESSION: fired once on mount with whatever is in storage.
        // If the stored tokens are expired and the refresh fails, Supabase will
        // fire TOKEN_REFRESH_FAILED → we must sign out there too.
        if (_event === "INITIAL_SESSION") {
          // If the access token is already expired, don't expose the stale session.
          // Supabase will attempt a refresh and fire TOKEN_REFRESHED or TOKEN_REFRESH_FAILED.
          const isExpired = session?.expires_at
            ? session.expires_at * 1000 < Date.now() + 5_000   // 5s clock-skew buffer
            : false;

          if (isExpired) {
            // Access token expired — Supabase is already attempting a silent refresh.
            // Keep loading=true and wait: TOKEN_REFRESHED or TOKEN_REFRESH_FAILED will fire shortly.
            // Do NOT set user/session here — that would expose the stale session.
            // Do NOT set loading=false — that would redirect the user before the refresh completes.
            return;
          }

          // On INITIAL_SESSION with a user, block rendering behind the disabled
          // check instead of showing the app for 1–2 frames. setTimeout(..., 0)
          // is still required to avoid the Web Locks re-entrancy deadlock on
          // sign-out, but we defer loading=false until the check completes.
          if (session?.user) {
            const u = session.user;
            setTimeout(async () => {
              const stillActive = await checkAccountStatus(u);
              if (stillActive) {
                setSession(session);
                setUser(u);
              }
              setLoading(false);
            }, 0);
          } else {
            setSession(null);
            setUser(null);
            setLoading(false);
          }
          return;
        }

        if (_event === "TOKEN_REFRESH_FAILED") {
          // Refresh token expired / revoked — force a clean sign-out so the
          // user lands on the login page instead of seeing a broken dashboard.
          // Surface it as a disabled reason so Login.tsx can show a banner.
          clearAuthStorage();
          setDisabledReason("session_expired");
          setLoading(false);
          import("@sentry/react").then(({ setUser }) => setUser(null));
          return;
        }

        // On sign-in events, run the disabled-account check before surfacing
        // the session to the app. Any other event (token refresh, etc.) is
        // safe to reflect immediately.
        if (_event === "SIGNED_IN" && session?.user) {
          const u = session.user;
          setTimeout(async () => {
            const stillActive = await checkAccountStatus(u);
            if (stillActive) {
              setSession(session);
              setUser(u);
              saveLeadSource(u.id);
              trackLogin(u.id, "magic_link");
              // Tawk.to context — use sessionStorage (cleared on tab close) to
              // limit exposure of the uid/email to long-lived XSS payloads.
              try {
                sessionStorage.setItem('te_user_id', u.id);
                if (u.email) sessionStorage.setItem('te_visitor_email', u.email);
              } catch { /* storage may be blocked */ }
              import("@sentry/react").then(({ setUser }) => {
                setUser({ id: u.id, email: u.email });
              });
            }
            setLoading(false);
          }, 0);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (_event === "SIGNED_OUT") {
          try {
            sessionStorage.removeItem('te_user_id');
            sessionStorage.removeItem('te_visitor_email');
          } catch { /* ignore */ }
          import("@sentry/react").then(({ setUser }) => setUser(null));
        }
      }
    );

    // NOTE: Do NOT call getSession() here separately.
    // onAuthStateChange fires INITIAL_SESSION synchronously, which is the
    // canonical Supabase v2 pattern for the initial auth state. Calling
    // getSession() in parallel creates a race condition where an expired
    // session from localStorage can set loading=false before the refresh
    // attempt completes, resulting in a briefly accessible dashboard with
    // no user context (ghost state).

    return () => {
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [checkAccountStatus, clearAuthStorage]);

  // Record general activity for sales follow-up automations (throttled to once
  // per 6h per user via localStorage so it doesn't write on every token refresh).
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    const key = `te_last_seen_${uid}`;
    const now = Date.now();
    try {
      const prev = Number(localStorage.getItem(key) || 0);
      if (now - prev < 6 * 60 * 60 * 1000) return;
      localStorage.setItem(key, String(now));
    } catch { /* storage may be blocked — still attempt the write */ }
    supabase.from("profiles").update({ last_seen_at: new Date(now).toISOString() } as never).eq("id", uid).then(undefined, () => {});
  }, [user?.id]);

  const signUp = useCallback(async (email: string, password: string, tracking?: TrackingParams) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });

    if (!error && data.user) {
      const trackingData = Object.fromEntries(
        Object.entries(tracking ?? {}).filter(([, v]) => v !== undefined)
      );
      // First-touch attribution (captured on the very first visit) + connect-from
      // geo survive to the DB even when the signup URL itself has no params.
      const firstTouch = getStoredLeadSource();
      const geo = await getVisitorGeo().catch(() => null);
      await supabase.from("registration_tracking").insert({
        user_id: data.user.id,
        ...trackingData,
        first_touch: firstTouch ?? null,
        country: geo?.country ?? firstTouch?.country ?? null,
        city: geo?.city ?? firstTouch?.city ?? null,
      } as any).then(undefined, () => {});
      supabase.functions.invoke("capture-signup-ip", { body: {} }).catch(() => {});
    }

    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data.user) {
      supabase.from("activity_logs").insert({
        user_id: data.user.id,
        action: "login",
        category: "auth",
        metadata: {},
      } as any).then(undefined, () => {});
      trackLogin(data.user.id, "password");
    }
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    setDisabledReason(null);
    try {
      // "local" scope avoids server roundtrip — important for expired sessions
      // where the server call would fail with 401.
      await supabase.auth.signOut({ scope: "local" });
    } catch (_err) {
      // Ignore errors — we always clear local state below regardless.
      // Common errors: AbortError (lock contention), 401 (expired), network issues.
    } finally {
      clearAuthStorage();
      // Clear user PII — migrated to sessionStorage; remove any legacy
      // localStorage entries written by older builds.
      try {
        sessionStorage.removeItem('te_user_id');
        sessionStorage.removeItem('te_visitor_email');
        localStorage.removeItem('te_user_id');
        localStorage.removeItem('te_visitor_email');
      } catch { /* ignore */ }
    }
    return { error: null };
  }, [clearAuthStorage]);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/portal/reset-password`,
    });
    return { error };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    loading,
    disabledReason,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
